import { useState } from 'react';
import type {
  ConfirmedSnapshot,
  DailyPurchaseGroup,
  PurchaseItem,
  WechatMessageKind,
  WechatPurchaseMessage,
} from '../types';
import { MEAL_LABELS } from '../types';
import { formatShortDate, weekdayLabel } from '../lib/date';
import { groupPurchaseItemsByCategory, purchaseDisplayCategory } from '../lib/ingredient-categories';
import { AlertIcon, BasketIcon, CheckIcon, ChevronRightIcon, CopyIcon, DownloadIcon, EditIcon, LinkIcon } from './Icons';
import { Modal } from './Modal';
import { QuantityAdjustModal } from './QuantityAdjustModal';

interface Props {
  /** Flattened compatibility input used by older callers and snapshots. */
  items: PurchaseItem[];
  dailyPurchaseGroups?: DailyPurchaseGroup[];
  extraPurchaseItems?: PurchaseItem[];
  warnings: string[];
  snapshot?: ConfirmedSnapshot;
  outputsStale?: boolean;
  saveState: 'saved' | 'saving' | 'error';
  confirming: boolean;
  actionBusy: string;
  onOverride: (key: string, quantity: string) => void;
  onResetOverride?: (key: string) => void;
  onConfirm: () => void;
  onCopy: (message?: WechatPurchaseMessage) => void;
  onExcel: () => void;
  onShare: () => void;
}

function belongsToWechatMessage(item: PurchaseItem, kind: WechatMessageKind): boolean {
  const category = purchaseDisplayCategory(item.ingredientCategory, item.name);
  if (kind === 'meat') return category === '荤菜';
  if (kind === 'vegetable') return category === '素菜';
  return category === '其他采购';
}

const WECHAT_MESSAGE_LABELS: Record<WechatMessageKind, string> = {
  meat: '荤菜',
  vegetable: '素菜',
  condiment: '其他采购',
};

function wechatMessageItemCount(snapshot: ConfirmedSnapshot, kind: WechatMessageKind): number {
  const hasGroupedItems = snapshot.dailyPurchaseGroups !== undefined
    || snapshot.extraPurchaseItems !== undefined;
  const messageItems = hasGroupedItems
    ? [
        ...(snapshot.dailyPurchaseGroups ?? []).flatMap((group) => group.items),
        ...(snapshot.extraPurchaseItems ?? []),
      ]
    : snapshot.purchaseItems;
  return messageItems.filter((item) => belongsToWechatMessage(item, kind)).length;
}

function wechatMessageDescription(kind: WechatMessageKind, itemCount: number): string {
  if (itemCount === 0) {
    return '本周无采购，可复制后直接发送';
  }
  if (kind === 'condiment') return `${itemCount} 项 · 含主食干货、调味品和其他 · 按天展示`;
  return `${itemCount} 项 · 按天展示`;
}

export function PurchaseSummary({
  items,
  dailyPurchaseGroups,
  extraPurchaseItems,
  warnings,
  snapshot,
  outputsStale = false,
  saveState,
  confirming,
  actionBusy,
  onOverride,
  onResetOverride,
  onConfirm,
  onCopy,
  onExcel,
  onShare,
}: Props) {
  const [sourceItem, setSourceItem] = useState<PurchaseItem | null>(null);
  const [quantityItem, setQuantityItem] = useState<PurchaseItem | null>(null);
  const groupedView = dailyPurchaseGroups !== undefined || extraPurchaseItems !== undefined;
  const groups = dailyPurchaseGroups ?? [];
  const extras = extraPurchaseItems ?? [];
  const visibleItems = groupedView ? [...groups.flatMap((group) => group.items), ...extras] : items;
  const selectionEmpty = visibleItems.length === 0;

  const renderRows = (rowItems: PurchaseItem[]) => (
    <div className="purchase-list">
      {rowItems.map((item) => (
        <button
          className={`purchase-row purchase-row--button${item.finalQuantity !== item.calculatedQuantity ? ' is-adjusted' : ''}`}
          type="button"
          key={item.key}
          onClick={() => setSourceItem(item)}
          aria-label={`查看${item.name}来源和调整数量`}
        >
          <strong className="purchase-row__name">{item.name}</strong>
          <strong className="purchase-row__quantity">
            {item.finalQuantity}<small>{item.unit}</small>
            {item.finalQuantity !== item.calculatedQuantity && <em>已调整</em>}
          </strong>
          <ChevronRightIcon className="purchase-row__chevron" size={19} />
        </button>
      ))}
    </div>
  );

  const renderCategoryRows = (rowItems: PurchaseItem[]) => (
    <div className="purchase-categories">
      {groupPurchaseItemsByCategory(rowItems).map((group) => (
        <section className="purchase-category" key={group.category}>
          <header className="purchase-category__header">
            <strong>{group.category}</strong><span>{group.items.length} 项</span>
          </header>
          {renderRows(group.items)}
        </section>
      ))}
    </div>
  );

  return (
    <aside className="summary-card">
      <header className="summary-card__header">
        <div className="summary-card__icon"><BasketIcon /></div>
        <div><p className="eyebrow">按天汇总</p><h2>采购清单</h2></div>
        <span className="summary-count">{groupedView ? `${groups.length} 天 · ${visibleItems.length} 项` : `${visibleItems.length} 项`}</span>
      </header>

      {warnings.length > 0 && (
        <div className="inline-alert inline-alert--warning"><AlertIcon size={18} /><span>{warnings.join('；')}</span></div>
      )}

      {selectionEmpty ? (
        <div className="summary-empty"><BasketIcon size={30} /><p>选好菜品或添加额外采购后，这里会按每天汇总食材。</p></div>
      ) : (
        <>
          {groupedView ? (
            <div className="purchase-groups">
              {groups.map((group) => (
                <section className="purchase-group" key={group.date}>
                  <header className="purchase-group__header">
                    <strong>{formatShortDate(group.date)} {weekdayLabel(group.date)}</strong>
                    <span>当日汇总 · {group.items.length} 项</span>
                  </header>
                  {renderCategoryRows(group.items)}
                </section>
              ))}
              {extras.length > 0 && (
                <section className="purchase-group purchase-group--extra">
                  <header className="purchase-group__header">
                    <strong>本周额外采购</strong>
                    <span>固定采购与临时补货 · {extras.length} 项</span>
                  </header>
                  {renderCategoryRows(extras)}
                </section>
              )}
            </div>
          ) : renderRows(items)}
        </>
      )}

      <div className="save-indicator" data-state={saveState}>
        {saveState === 'saving' ? <span className="spinner spinner--small" /> : saveState === 'error' ? <AlertIcon size={15} /> : <CheckIcon size={15} />}
        {saveState === 'saving' ? '正在自动保存' : saveState === 'error' ? '自动保存失败' : '所有修改已保存'}
      </div>

      <button className="button button--primary button--confirm" type="button" disabled={selectionEmpty || confirming || saveState === 'saving'} onClick={onConfirm}>
        {confirming ? <><span className="spinner spinner--button" /> 正在确认</> : snapshot ? '重新确认并生成新版本' : '确认计划并生成采购单'}
      </button>
      {snapshot && (
        <section className="output-actions">
          {outputsStale && (
            <div className="inline-alert inline-alert--warning stale-output-warning" data-qa="stale-output-warning" role="status">
              <AlertIcon size={19} />
              <span><strong>采购内容已变化</strong><small>请先重新确认；旧版复制、Excel 和分享暂不可用。</small></span>
            </div>
          )}
          <div className={`confirmed-label ${outputsStale ? 'is-stale' : ''}`}><CheckIcon size={15} /> {outputsStale ? `旧的第 ${snapshot.version} 版采购单` : `已生成第 ${snapshot.version} 版采购单`}</div>
          {snapshot.wechatMessages !== undefined ? (
            <section className="wechat-output-actions" aria-label="微信采购消息">
              <header className="wechat-output-actions__header">
                <strong>微信采购消息</strong>
                <small>分三条发送，每条均按天展示</small>
              </header>
              {snapshot.wechatMessages.map((message) => {
                const itemCount = wechatMessageItemCount(snapshot, message.kind);
                return (
                  <button
                    className={`output-button output-button--wechat output-button--wechat-${message.kind}`}
                    type="button"
                    key={message.kind}
                    onClick={() => onCopy(message)}
                    disabled={Boolean(actionBusy) || outputsStale}
                    aria-label={`复制${WECHAT_MESSAGE_LABELS[message.kind]}消息`}
                  >
                    <CopyIcon />
                    <span>
                      <strong>复制{WECHAT_MESSAGE_LABELS[message.kind]}消息</strong>
                      <small>{wechatMessageDescription(message.kind, itemCount)}</small>
                    </span>
                    <em className={itemCount === 0 ? 'is-empty' : ''}>{itemCount === 0 ? '无采购' : `${itemCount} 项`}</em>
                  </button>
                );
              })}
            </section>
          ) : (
            <button className="output-button" type="button" onClick={() => onCopy()} disabled={Boolean(actionBusy) || outputsStale}><CopyIcon /><span><strong>复制历史微信文案</strong><small>保持该版本原有文案</small></span></button>
          )}
          <button className="output-button" type="button" onClick={onExcel} disabled={Boolean(actionBusy) || outputsStale}><DownloadIcon /><span><strong>{actionBusy === 'excel' ? '正在生成…' : '下载 Excel'}</strong><small>含每日汇总与计算明细</small></span></button>
          <button className="output-button" type="button" onClick={onShare} disabled={Boolean(actionBusy) || outputsStale}><LinkIcon /><span><strong>{actionBusy === 'share' ? '正在生成…' : '分享采购网页'}</strong><small>工作人员只读查看</small></span></button>
        </section>
      )}

      <Modal
        open={Boolean(sourceItem)}
        title={sourceItem ? `${sourceItem.name} ${sourceItem.finalQuantity}${sourceItem.unit}` : '采购来源'}
        eyebrow="用量来源"
        onClose={() => setSourceItem(null)}
      >
        {sourceItem && (
          <div className="source-detail">
            <button
              className="source-detail__adjust-button"
              type="button"
              onClick={() => {
                setQuantityItem(sourceItem);
                setSourceItem(null);
              }}
            >
              <EditIcon size={20} />
              <span>
                <strong>调整采购数量</strong>
                <small>只修改这次采购，不改变菜品配方</small>
              </span>
              <ChevronRightIcon size={20} />
            </button>
            {sourceItem.calculatedQuantity !== sourceItem.finalQuantity && (
              <div className="source-detail__adjustment">
                <span>自动合计<strong>{sourceItem.calculatedQuantity}{sourceItem.unit}</strong></span>
                <span>{sourceItem.sources?.some((source) => source.kind !== 'extra') ? '当天调整' : '本周调整'}<strong>{sourceItem.finalQuantity}{sourceItem.unit}</strong></span>
                <span>最终采购<strong>{sourceItem.finalQuantity}{sourceItem.unit}</strong></span>
              </div>
            )}
            <h3>来自以下安排</h3>
            {sourceItem.sources?.length ? (
              <div className="source-detail__list">
                {sourceItem.sources.map((source, index) => (
                  <article key={`${source.kind}-${source.date ?? 'week'}-${source.meal ?? 'extra'}-${source.dishId}-${index}`}>
                    <p>{source.kind === 'extra' ? '本周额外采购' : `${formatShortDate(source.date ?? '')} ${weekdayLabel(source.date ?? '')}${source.meal ? ` · ${MEAL_LABELS[source.meal]}` : ''}`}</p>
                    <strong>
                      {source.kind === 'daily_default'
                        ? '当天固定采购 · 自动加入'
                        : source.kind === 'extra' && source.dishId.startsWith('extra_item_')
                          ? source.dishName
                          : `${source.dishName} · ${source.batchCount}批`}
                    </strong>
                    <small>贡献 {source.contributionQuantity}{source.unit}</small>
                  </article>
                ))}
              </div>
            ) : <p className="source-detail__empty">当前版本没有保存更细的来源记录。</p>}
          </div>
        )}
      </Modal>

      <QuantityAdjustModal
        item={quantityItem}
        onClose={() => setQuantityItem(null)}
        onApply={onOverride}
        onReset={onResetOverride}
      />
    </aside>
  );
}
